package com.rosario.juridico.domain

import juridico5.composeapp.generated.resources.Res
import kotlinx.datetime.*
import kotlin.math.pow

object SmvmLogic {

    suspend fun leerSMVM(): String {
        return try {
            Res.readBytes("files/smvm_base.txt").decodeToString()
        } catch (e: Exception) {
            ""
        }
    }

    fun parsearSMVM(texto: String): Map<String, SmvmItem> {
        return texto.lines()
            .map { it.trim() }
            .filter { it.isNotBlank() && !it.startsWith("#") }
            .mapNotNull { linea ->
                val parts = linea.split("|")
                if (parts.size >= 2) {
                    val periodo = parts[0].trim()
                    val monto = parts[1].trim().toDoubleOrNull()
                    if (monto != null && periodo.matches(Regex("\\d{4}-\\d{2}"))) {
                        periodo to SmvmItem(periodo, monto, true)
                    } else null
                } else null
            }.toMap()
    }

    fun proyectarSMVM(periodo: String, datos: Map<String, SmvmItem>): SmvmItem {
        val ultimoDisponible = datos.keys.maxOrNull() ?: return SmvmItem(periodo, 0.0, false)
        val ultimoValor = datos[ultimoDisponible]?.smvm ?: 0.0
        
        val mesesDif = calcularMesesEntre(ultimoDisponible, periodo)
        // Proyección conservadora: 2% mensual si es a futuro
        val montoProyectado = if (mesesDif > 0) {
            ultimoValor * (1.02).pow(mesesDif.toDouble())
        } else {
            ultimoValor
        }
        
        return SmvmItem(periodo, montoProyectado, false)
    }

    private fun calcularMesesEntre(inicio: String, fin: String): Int {
        try {
            val p1 = inicio.split("-")
            val p2 = fin.split("-")
            if (p1.size != 2 || p2.size != 2) return 0
            val a1 = p1[0].toInt(); val m1 = p1[1].toInt()
            val a2 = p2[0].toInt(); val m2 = p2[1].toInt()
            return (a2 - a1) * 12 + (m2 - m1)
        } catch (e: Exception) {
            return 0
        }
    }

    fun siguientePeriodo(periodo: String): String {
        val parts = periodo.split("-")
        if (parts.size != 2) return periodo
        var year = parts[0].toInt()
        var month = parts[1].toInt()
        month++
        if (month > 12) {
            month = 1
            year++
        }
        return "$year-${month.toString().padStart(2, '0')}"
    }

    fun calcularLiquidacion(
        montoPorcentaje: Double,
        fechaInicio: LocalDate,
        fechaFin: LocalDate,
        pagos: List<PagoParcial>,
        datosSMVM: Map<String, SmvmItem>,
        porcentajeHonorarios: Double
    ): ResultadoCalculoSMVM {
        val renglones = mutableListOf<RenglonCalculoSMVM>()
        
        // Normalizamos a primer día del mes para iterar
        var current = LocalDate(fechaInicio.year, fechaInicio.month, 1)
        val end = LocalDate(fechaFin.year, fechaFin.month, 1)
        
        var subtotalCuotas = 0.0
        var subtotalPagos = 0.0

        while (current <= end) {
            val periodoActual = "${current.year}-${current.monthNumber.toString().padStart(2, '0')}"
            val smvmItem = datosSMVM[periodoActual] ?: proyectarSMVM(periodoActual, datosSMVM)
            val cuota = smvmItem.smvm * (montoPorcentaje / 100.0)
            
            val pagosDelMes = pagos.filter { 
                it.fecha.year == current.year && it.fecha.monthNumber == current.monthNumber
            }.sumOf { it.monto }
            
            val saldo = cuota - pagosDelMes
            
            renglones.add(RenglonCalculoSMVM(
                periodo = periodoActual,
                smvm = smvmItem.smvm,
                porcentaje = montoPorcentaje,
                cuotaCalculada = cuota,
                pagosEfectuados = pagosDelMes,
                interes = 0.0,
                saldo = saldo,
                esEstimado = !smvmItem.esOficial
            ))
            
            subtotalCuotas += cuota
            subtotalPagos += pagosDelMes
            
            current = if (current.monthNumber == 12) {
                LocalDate(current.year + 1, 1, 1)
            } else {
                LocalDate(current.year, current.monthNumber + 1, 1)
            }
        }

        val deudaTotal = renglones.sumOf { it.saldo }
        val honorarios = deudaTotal * (porcentajeHonorarios / 100.0)

        return ResultadoCalculoSMVM(
            renglones = renglones,
            deudaTotal = deudaTotal,
            subtotalCuotas = subtotalCuotas,
            subtotalPagos = subtotalPagos,
            honorarios = honorarios
        )
    }
}
